"use client";
import React from "react";

import { Box, BoxSx } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { useFocusControls } from "../../hooks";
import { useId } from "../../hooks/useId";
import { TextFieldProps } from "../TextField";
import CustomCheckmarkIcon from "./assets/CustomCheckmarkIcon";
import CustomIndeterminateIcon from "./assets/CustomIndeterminateIcon";

export interface CheckboxProps
  extends Omit<TextFieldProps, "label" | "error" | "value"> {
  label: React.ReactNode;
  value?: boolean;
  indeterminate?: boolean;
  error?: boolean;
  id?: string;
  ariaLabel?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  value,
  onChange,
  onBlur,
  indeterminate = false,
  error = false,
  disabled,
  required,
  id,
  ariaLabel
}) => {
  const base0Id = useId();
  const inputId = id ?? base0Id;

  const [isOutlineVisible, preventOutlineOnClick, handleFocus, handleBlur] =
    useFocusControls(onBlur);

  const isFilled = value || indeterminate;

  const checkboxStyles = React.useMemo<BoxSx>(() => {
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "spacing-6",
      minWidth: "spacing-6",
      height: "spacing-6",
      borderStyle: "solid",
      borderWidth: "2px", // does not take spacing-1
      color: "input-onSurface",
      borderColor: error
        ? "input-stroke-error"
        : isFilled
          ? "input-surface-enabled"
          : "input-stroke-enabled",
      borderRadius: "radius-1",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "transition-color-medium",
      backgroundColor: isFilled
        ? error
          ? "input-surface-error"
          : "input-surface-enabled"
        : undefined,
      ...(error
        ? {}
        : {
            ":hover": {
              backgroundColor: isFilled ? "input-surface-hover" : undefined,
              borderColor: isFilled
                ? "input-surface-hover"
                : "input-stroke-hover"
            },
            ":active": {
              borderColor: "input-surface-click",
              backgroundColor: "input-surface-click"
            }
          }),
      ...(isOutlineVisible && {
        outlineStyle: "solid",
        borderRadius: "radius-1",
        outlineColor: "focus",
        outlineWidth: "1px",
        outlineOffset: "1px"
      })
    };
  }, [error, isFilled, disabled, isOutlineVisible]);

  const labelStyles = React.useMemo<BoxSx>(() => {
    return {
      display: "inline-flex",
      alignItems: "stretch",
      color: disabled ? "input-text-disabled" : "input-text",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? "0.5" : "1",
      fontSize: "font-size-1", // TODO: verify
      transition: "transition-color-medium",
      userSelect: "none"
    };
  }, [disabled]);

  const checkboxWrapperStyles = React.useMemo<BoxSx>(() => {
    return {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "fit-content"
    };
  }, []);

  return (
    <Box
      onMouseDown={preventOutlineOnClick}
      onMouseUp={preventOutlineOnClick}
      sx={checkboxWrapperStyles}
    >
      <Box
        as="label"
        htmlFor={inputId}
        id={`${inputId}-label`}
        sx={labelStyles}
      >
        <Box
          as="span"
          sx={{
            display: "flex",
            flexShrink: 0,
            alignItems: "center"
          }}
        >
          <Box
            as="input"
            type="checkbox"
            id={inputId}
            checked={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            aria-invalid={!!error}
            aria-checked={value}
            aria-disabled={disabled}
            aria-required={required}
            aria-label={ariaLabel}
          />
          <Box as="span" sx={checkboxStyles}>
            {value && <CustomCheckmarkIcon />}
            {!value && indeterminate && <CustomIndeterminateIcon />}
          </Box>
        </Box>
        {label && (
          <Text
            variant="body-1-regular"
            as="span"
            sx={{
              ...labelStyles,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              ml: 2
            }}
          >
            {label}
          </Text>
        )}
      </Box>
    </Box>
  );
};
